import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { TYPING_CLEAR_MS } from '../lib/constants';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SocketProvider = ({ children }) => {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const socketRef = useRef(null);

  useEffect(() => {
    if (!user || user === false) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setConnected(false);
      }
      return undefined;
    }

    const s = io(API_URL, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    s.on('connect', () => setConnected(true));
    s.on('disconnect', () => setConnected(false));
    s.on('connect_error', () => { /* handled by reconnection */ });
    s.on('authenticated', () => { /* noop */ });
    s.on('error', (data) => {
      // Server-side rejections (unauthorized chat, invalid media, ...).
      toast.error(data?.message || 'Message could not be delivered');
    });

    s.on('new_message', (message) => {
      setMessages(prev => [...prev, message]);
    });

    s.on('message_deleted', (data) => {
      setMessages(prev =>
        prev.map(msg =>
          msg.id === data.message_id
            ? { ...msg, is_deleted: true, content: '[Message deleted]' }
            : msg
        )
      );
    });

    s.on('user_typing', (data) => {
      setTypingUsers(prev => ({ ...prev, [data.user_id]: data.user_name }));
      setTimeout(() => {
        setTypingUsers(prev => {
          const updated = { ...prev };
          delete updated[data.user_id];
          return updated;
        });
      }, TYPING_CLEAR_MS);
    });

    s.on('user_stopped_typing', (data) => {
      setTypingUsers(prev => {
        const updated = { ...prev };
        delete updated[data.user_id];
        return updated;
      });
    });

    s.on('user_online', (data) => {
      setOnlineUsers(prev => new Set(prev).add(data.user_id));
    });

    s.on('user_offline', (data) => {
      setOnlineUsers(prev => {
        const updated = new Set(prev);
        updated.delete(data.user_id);
        return updated;
      });
    });

    socketRef.current = s;

    return () => {
      s.disconnect();
      socketRef.current = null;
    };
  }, [user]);

  const emit = useCallback((event, payload) => {
    if (socketRef.current && connected) {
      socketRef.current.emit(event, payload);
      return true;
    }
    return false;
  }, [connected]);

  const joinChat = useCallback((chatId) => emit('join_chat', { chat_id: chatId }), [emit]);
  const leaveChat = useCallback((chatId) => emit('leave_chat', { chat_id: chatId }), [emit]);
  const sendMessage = useCallback((chatId, content, mediaId = null) => {
    const sent = emit('send_message', { chat_id: chatId, content, media_id: mediaId });
    if (!sent) {
      toast.error('Not connected. Your message was not sent — please try again.');
    }
    return sent;
  }, [emit]);
  const startTyping = useCallback((chatId) => emit('typing_start', { chat_id: chatId }), [emit]);
  const stopTyping = useCallback((chatId) => emit('typing_stop', { chat_id: chatId }), [emit]);
  const deleteMessage = useCallback((messageId) => emit('delete_message', { message_id: messageId }), [emit]);

  const value = {
    socket: socketRef.current,
    connected,
    messages,
    setMessages,
    typingUsers,
    onlineUsers,
    joinChat,
    leaveChat,
    sendMessage,
    startTyping,
    stopTyping,
    deleteMessage,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};
